import React, { createContext, useState, useMemo, useContext, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { buildFilters, handleFilterData, INITIAL_FILTERS_STATE, INITIAL_MATCH_SETTINGS_STATE } from '../components/CostBenchmark/costBenchmarkHelpers.js';
import { getAllProgramsByBudgetId } from '../services/programService.js';
import { COST_BENCHMARK_PAGE_LENGTH } from '../components/CostBenchmark/costBenchmarkTexts.js';
import { FiltersContext } from '../components/reusable/Filters/filterContext.js';
import { useUser } from './userContext.js';
import { LoaderContext } from './loaderContext.js';
import { fetchEventSource } from '@microsoft/fetch-event-source';

const parseBenchmarkStreamData = (data) => {
  let parsedData = JSON.parse(data);

  // The recommendation service currently serializes the message payload before
  // wrapping it in the SSE event, so support both single- and double-encoded JSON.
  if (typeof parsedData === 'string') {
    parsedData = JSON.parse(parsedData);
  }

  return Array.isArray(parsedData) ? parsedData : [parsedData];
};

export const CostBenchmarkContext = createContext();

export const CostBenchmarkContextProvider = ({ children }) => {
  const { setLoading } = useContext(LoaderContext);
  const [activeTooltip, setActiveTooltip] = useState({ visible: false, targetId: null, data: null });
  const [populationBoundaries, setPopulationBoundaries] = useState();
  const [activeTab, setActiveTab] = useState(0);
  const [benchmarkData, setBenchmarkData] = useState([]);
  const [currPage, setCurrPage] = useState(0);
  const [loadedPrograms, setLoadedPrograms] = useState({});
  const [budgetHasPrograms, setBudgetHasPrograms] = useState(false);
  const [matchSettings, setMatchSettings] = useState(INITIAL_MATCH_SETTINGS_STATE);
  const [defaultFilters, setDefaultFilters] = useState(INITIAL_FILTERS_STATE);
  const [AIAnalysisData, setAIAnalysisData] = useState({});
  const [refreshAIAnalysis, setRefreshAIAnalysis] = useState(false);
  const streamAbortControllerRef = useRef(null);
  const { user } = useUser();
  const { appliedFilters } = useContext(FiltersContext);

  const filteredData = useMemo(() =>
    handleFilterData(benchmarkData, appliedFilters),
  [benchmarkData, appliedFilters]
  );

  const checkBudgetPrograms = useCallback(async (budgetId) => {
    if (!budgetId) return;

    const resp = await getAllProgramsByBudgetId(budgetId);
    setBudgetHasPrograms(Boolean(resp.data.length));
  }, []);

  const paginatedPrograms = useMemo(() => {
    const startInd = currPage * COST_BENCHMARK_PAGE_LENGTH;
    return filteredData.slice(startInd, startInd + COST_BENCHMARK_PAGE_LENGTH);
  }, [filteredData, currPage]);

  const goToPage = useCallback((pageNum) => {
    if (!filteredData || filteredData.length === 0) return;

    const pageSize = COST_BENCHMARK_PAGE_LENGTH;
    const totalPages = Math.ceil(filteredData.length / pageSize);

    let targetPage = Math.max(0, Math.min(pageNum, totalPages - 1));
    setCurrPage(targetPage);
  }, [filteredData]);

  const fetchGraphicTabData = useCallback(async (filters) => {
    streamAbortControllerRef.current?.abort();
    const abortController = new AbortController();
    streamAbortControllerRef.current = abortController;

    const finishStream = () => {
      if (streamAbortControllerRef.current !== abortController) return;

      streamAbortControllerRef.current = null;
      setLoading(false);
    };

    try {
      setLoading(true);
      setBenchmarkData([]);
      setAIAnalysisData({});
      setLoadedPrograms({});
      setRefreshAIAnalysis(true);
      setCurrPage(0);
      const data = buildFilters(filters);
      data.organization = user?.activeOrganization;
      const activeCostCenterIds = user?.activeCostCenterIds || [];

      await fetchEventSource('/api/programs/benchmark-analysis', {
        method: 'POST',
        headers: {
          'Accept': 'text/event-stream',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ payload: { benchmarkType: data }, activeCostCenterIds }),
        signal: abortController.signal,
        openWhenHidden: true,
        onmessage(message) {
          if (streamAbortControllerRef.current !== abortController) return;
          if (message.event && message.event !== 'message') return;

          const streamedPrograms = parseBenchmarkStreamData(message.data);
          setBenchmarkData((previousData) => [...previousData, ...streamedPrograms]);
          setLoading(false);
        },
        onclose() {
          finishStream();
        },
        onerror(error) {
          finishStream();
          throw error;
        }
      });
    } catch {
      finishStream();
    }
  }, [setLoading, user?.activeOrganization, user?.activeCostCenterIds]);


  useEffect(() => {
    setCurrPage(0);
    setLoadedPrograms({});
  }, [appliedFilters]);

  useEffect(() => () => {
    const abortController = streamAbortControllerRef.current;
    streamAbortControllerRef.current = null;
    abortController?.abort();
  }, []);

  const value = useMemo(() => ({
    activeTooltip,
    setActiveTooltip,
    benchmarkData,
    setBenchmarkData,
    populationBoundaries,
    setPopulationBoundaries,
    activeTab,
    setActiveTab,
    fetchGraphicTabData,
    paginatedPrograms,
    currPage,
    goToPage,
    filteredData,
    loadedPrograms,
    setLoadedPrograms,
    setCurrPage,
    checkBudgetPrograms,
    budgetHasPrograms,
    matchSettings,
    setMatchSettings,
    defaultFilters,
    setDefaultFilters,
    AIAnalysisData,
    setAIAnalysisData,
    refreshAIAnalysis,
    setRefreshAIAnalysis,
  }), [
    activeTooltip,
    benchmarkData,
    populationBoundaries,
    activeTab,
    fetchGraphicTabData,
    paginatedPrograms,
    currPage,
    goToPage,
    filteredData,
    loadedPrograms,
    setLoadedPrograms,
    budgetHasPrograms,
    matchSettings,
    defaultFilters,
    setDefaultFilters,
    AIAnalysisData,
    setAIAnalysisData,
    refreshAIAnalysis,
    setRefreshAIAnalysis,
  ]);

  return (
    <CostBenchmarkContext.Provider value={value}>
      {children}
    </CostBenchmarkContext.Provider>
  );
};

CostBenchmarkContextProvider.propTypes = {
  children: PropTypes.node
};
